/**
 * AUTHENTICATION PAGE
 * 
 * Full-page login/signup with:
 * - Email verification on signup
 * - Forgot password functionality
 * - Required Terms of Service agreement
 * - Glassmorphic styling matching the app
 */

import React, { useState, useEffect } from 'react';
import { signUpWithEmail, signInWithEmail, sendVerificationEmail, sendPasswordReset, getCurrentUser } from '../firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowLeft, Mail, Sparkles, Key, User, AtSign, CheckCircle, XCircle } from 'lucide-react';
import { AppIcons } from '../constants/icons';

type PageView = 'auth' | 'forgotPassword' | 'onboarding';

const Auth: React.FC = () => {
  const navigate = useNavigate();
  
  // Page view state
  const [view, setView] = useState<PageView>('auth');
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Status state
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Forgot password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Onboarding state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Username validation
  const validateUsername = (value: string): string => {
    if (value.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (value.length > 20) {
      return 'Username must be 20 characters or less';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'Only letters, numbers, and underscores allowed';
    }
    return '';
  };

  // Check username availability
  useEffect(() => {
    if (view !== 'onboarding' || !username) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }

    const checkUsername = async () => {
      const error = validateUsername(username);
      if (error) {
        setUsernameError(error);
        setUsernameAvailable(null);
        return;
      }

      setUsernameChecking(true);
      setUsernameError('');

      try {
        const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
        const usernameDoc = await getDoc(usernameDocRef);

        if (usernameDoc.exists()) {
          setUsernameAvailable(false);
          setUsernameError('Username is already taken');
        } else {
          setUsernameAvailable(true);
        }
      } catch (error) {
        console.error('Error checking username:', error);
      } finally {
        setUsernameChecking(false);
      }
    };

    const debounce = setTimeout(checkUsername, 500);
    return () => clearTimeout(debounce);
  }, [username, view]);

  // Handle onboarding form submission
  const handleOnboardingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const user = getCurrentUser();
    if (!user) {
      setMessage('❌ Not logged in. Please try again.');
      return;
    }

    if (!usernameAvailable) {
      setMessage('❌ Please choose an available username.');
      return;
    }

    setIsSavingProfile(true);
    setMessage('');

    try {
      // Reserve the username
      const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
      await setDoc(usernameDocRef, {
        uid: user.uid,
        reservedAt: Timestamp.now()
      });

      // Create/update user profile
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        username,
        usernameLower: username.toLowerCase(),
        displayName: displayName.trim() || username,
        email: user.email,
        createdAt: Timestamp.now(),
        usernameLastChanged: Timestamp.now(),
        agreedToTermsAt: Timestamp.now(),
        profile: {
          username,
          usernameLower: username.toLowerCase(),
          displayName: displayName.trim() || username,
          bio: '',
          avatarUrl: '',
          bannerUrl: '',
          socialLinks: {}
        },
        privacy: {
          isPublic: false,
          showCollection: false,
          showWishlist: false,
          showDestashList: false,
          showFavorites: false,
          showDestashArchive: false,
          showProjects: false,
          showStats: false,
          allowMessages: true
        },
        preferences: {
          theme: 'light',
          defaultItemsPerPage: 20,
          emailNotifications: {
            destashInquiry: true,
            weeklyDigest: false
          }
        }
      }, { merge: true });

      setMessage('✅ Profile created!');
      
      // Redirect to collection after short delay
      setTimeout(() => {
        navigate('/collection');
      }, 1000);
      
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setMessage(`❌ Failed to save profile: ${error.message}`);
      setIsSavingProfile(false);
    }
  };

  // Handle auth form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    // Validate password match for sign up
    if (isSignUp && password !== confirmPassword) {
      setMessage('❌ Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setMessage('❌ Password must be at least 8 characters');
      return;
    }

    // Validate terms agreement for sign up
    if (isSignUp && !agreedToTerms) {
      setMessage('❌ Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        await signUpWithEmail(email, password);
        
        // Send verification email
        try {
          await sendVerificationEmail();
        } catch (verifyError) {
          console.error('Error sending verification email:', verifyError);
          // Continue anyway - they can request another later
        }
        
        setMessage('✅ Account created! Now let\'s set up your profile.');
        
        // Move to onboarding step
        setTimeout(() => {
          setView('onboarding');
          setMessage('');
          setIsLoading(false);
        }, 500);
      } else {
        // Sign in
        await signInWithEmail(email, password);
        setMessage('✅ Logged in! Redirecting...');
        
        // Redirect to collection
        setTimeout(() => {
          navigate('/collection');
        }, 500);
      }
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
      setIsLoading(false);
    }
  };

  // Handle forgot password submission
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      await sendPasswordReset(forgotPasswordEmail);
      setForgotPasswordSent(true);
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home Link */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-glass-secondary hover:text-glass-secondary mb-6 transition-default font-body"
        >
          <ArrowLeft className="icon-sm" />
          Back to Home
        </Link>

        {/* Main Card */}
        <div className="glass-card p-8">
          
          {/* AUTH VIEW */}
          {view === 'auth' && (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="text-5xl mb-3">💅</div>
                <h1
                  className="text-2xl font-bold text-glass font-heading flex items-center justify-center gap-2"
                >
                  {isSignUp ? (
                    <>
                      <AppIcons.Join className="w-6 h-6" />
                      Join the Collection
                    </>
                  ) : (
                    <>
                      <AppIcons.Welcome className="w-6 h-6" />
                      Welcome Back
                    </>
                  )}
                </h1>
                <p
                  className="text-glass-muted mt-2 font-body"
                >
                  {isSignUp ? 'Create your free account' : 'Sign in to your account'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label 
                    className="block text-sm font-bold text-glass-secondary mb-2 font-body"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-glass rounded-xl font-body"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Password */}
                <div>
                  <label 
                    className="block text-sm font-bold text-glass-secondary mb-2 font-body"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="input-glass rounded-xl pr-12 font-body"
                      placeholder={isSignUp ? 'At least 8 characters' : '••••••••'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-glass-muted hover:text-glass-secondary transition-default"
                    >
                      {showPassword ? <Eye className="icon-md" /> : <EyeOff className="icon-md" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password - Only for sign up */}
                {isSignUp && (
                  <div>
                    <label 
                      className="block text-sm font-bold text-glass-secondary mb-2 font-body"
                    >
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className={`input-glass rounded-xl pr-12 ${
                          confirmPassword && password !== confirmPassword
                            ? 'border-red-400 focus:ring-red-500'
                            : confirmPassword && password === confirmPassword
                              ? 'border-green-400 focus:ring-green-500'
                              : ''
                        }`}
                        placeholder="Re-enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-glass-muted hover:text-glass-secondary transition-default"
                      >
                        {showConfirmPassword ? <Eye className="icon-md" /> : <EyeOff className="icon-md" />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                    )}
                    {confirmPassword && password === confirmPassword && (
                      <p className="text-green-600 text-sm mt-1">✓ Passwords match</p>
                    )}
                  </div>
                )}

                {/* Terms of Service Agreement - Only for sign up */}
                {isSignUp && (
                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-pink-500 cursor-pointer"
                    />
                    <label 
                      htmlFor="terms-agreement" 
                      className="text-sm text-glass-secondary font-body cursor-pointer"
                    >
                      I agree to the{' '}
                      <Link 
                        to="/terms" 
                        target="_blank"
                        className="text-glass hover:underline font-medium"
                      >
                        Terms of Service
                      </Link>
                      ,{' '}
                      <Link 
                        to="/privacy" 
                        target="_blank"
                        className="text-glass hover:underline font-medium"
                      >
                        Privacy Policy
                      </Link>
                      ,{' '}
                      <Link 
                        to="/refund-policy" 
                        target="_blank"
                        className="text-glass hover:underline font-medium"
                      >
                        Refund Policy
                      </Link>
                      , and{' '}
                      <Link 
                        to="/copyright" 
                        target="_blank"
                        className="text-glass hover:underline font-medium"
                      >
                        Copyright Policy
                      </Link>
                    </label>
                  </div>
                )}

                {/* Message */}
                {message && (
                  <div
                    className={message.startsWith('✅') ? 'alert-success' : 'alert-error'}
                  >
                    {message}
                    {message.startsWith('✅') && isSignUp && (
                      <p className="mt-2 text-sm flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <strong>Tip:</strong> Check your spam/junk folder for the verification email!
                      </p>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || (isSignUp && password !== confirmPassword) || (isSignUp && !confirmPassword) || (isSignUp && !agreedToTerms)}
                  className="btn-primary w-full rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-body"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {isSignUp ? 'Creating account...' : 'Logging in...'}
                    </>
                  ) : isSignUp ? (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Sign Up
                    </>
                  ) : (
                    <>
                      <Key className="w-5 h-5" />
                      Log In
                    </>
                  )}
                </button>
              </form>

              {/* Links */}
              <div className="mt-6 text-center space-y-3">
                {/* Forgot Password - only show on login */}
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      setView('forgotPassword');
                      setForgotPasswordEmail(email);
                      setMessage('');
                      setForgotPasswordSent(false);
                    }}
                    className="text-sm text-glass-muted hover:text-glass-secondary font-medium font-body"
                  >
                    Forgot your password?
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setConfirmPassword('');
                    setMessage('');
                    setAgreedToTerms(false);
                  }}
                  className="text-sm text-glass-secondary hover:text-glass font-medium block w-full font-body"
                >
                  {isSignUp ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
                </button>
              </div>
            </>
          )}

          {/* FORGOT PASSWORD VIEW */}
          {view === 'forgotPassword' && (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="text-5xl mb-3 flex justify-center">
                  <Key className="w-12 h-12 text-glass" />
                </div>
                <h1
                  className="text-2xl font-bold text-glass font-heading"
                >
                  Reset Password
                </h1>
              </div>

              {!forgotPasswordSent ? (
                <>
                  <p 
                    className="text-glass-secondary mb-6 text-center font-body"
                  >
                    Enter your email address and we'll send you a link to reset your password.
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label 
                        className="block text-sm font-bold text-glass-secondary mb-2 font-body"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        required
                        className="input-glass rounded-xl font-body"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Message */}
                    {message && (
                      <div className="alert-error font-body">
                        {message}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading || !forgotPasswordEmail}
                      className="btn-primary w-full rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-body"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          Send Reset Link
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => {
                        setView('auth');
                        setMessage('');
                      }}
                      className="text-sm text-glass-muted hover:text-glass-secondary font-medium font-body"
                    >
                      ← Back to Login
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-4">
                    <div className="text-5xl mb-4 flex justify-center">
                      <Mail className="w-12 h-12 text-glass" />
                    </div>
                    <h3
                      className="text-xl font-bold text-glass mb-2 font-body"
                    >
                      Check your inbox!
                    </h3>
                    <p
                      className="text-glass-secondary mb-2 font-body"
                    >
                      We've sent a password reset link to:
                    </p>
                    <p
                      className="font-bold text-glass-secondary mb-4 font-body"
                    >
                      {forgotPasswordEmail}
                    </p>
                    <p
                      className="text-glass-muted text-sm mb-6 font-body flex items-center justify-center gap-1"
                    >
                      <Mail className="w-4 h-4" />
                      <strong>Tip:</strong> Check your spam/junk folder if you don't see it!
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setView('auth');
                      setForgotPasswordSent(false);
                      setMessage('');
                    }}
                    className="btn-primary w-full rounded-xl font-body"
                  >
                    ← Back to Login
                  </button>
                </>
              )}
            </>
          )}

          {/* ONBOARDING VIEW */}
          {view === 'onboarding' && (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="text-5xl mb-3">✨</div>
                <h1 className="text-2xl font-bold text-glass font-heading">
                  Set Up Your Profile
                </h1>
                <p className="text-glass-muted mt-2 font-body">
                  Choose a username to get started
                </p>
              </div>

              <form onSubmit={handleOnboardingSubmit} className="space-y-5">
                {/* Username */}
                <div>
                  <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                    <AtSign className="icon-sm inline mr-1" />
                    Username <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.trim())}
                      maxLength={20}
                      required
                      className={`input-glass rounded-xl pr-10 ${
                        usernameError
                          ? 'border-red-400 focus:border-red-400'
                          : usernameAvailable
                            ? 'border-green-400 focus:border-green-400'
                            : ''
                      }`}
                      placeholder="your_username"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {usernameChecking ? (
                        <Loader2 className="w-5 h-5 text-glass-muted animate-spin" />
                      ) : usernameError ? (
                        <XCircle className="icon-md text-red-400" />
                      ) : usernameAvailable ? (
                        <CheckCircle className="icon-md text-green-400" />
                      ) : null}
                    </div>
                  </div>
                  {usernameError && (
                    <p className="text-red-400 text-sm mt-1">{usernameError}</p>
                  )}
                  {usernameAvailable && !usernameError && (
                    <p className="text-green-400 text-sm mt-1">✓ Username available!</p>
                  )}
                  <p className="text-glass-muted text-xs mt-1">
                    3-20 characters. Letters, numbers, and underscores only.
                  </p>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-bold text-glass-secondary mb-2 font-body">
                    <User className="icon-sm inline mr-1" />
                    Display Name <span className="text-glass-muted font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={50}
                    className="input-glass rounded-xl font-body"
                    placeholder="How you want to be called"
                  />
                  <p className="text-glass-muted text-xs mt-1">
                    This is what others will see. You can change it anytime.
                  </p>
                </div>

                {/* Message */}
                {message && (
                  <div className={message.startsWith('✅') ? 'alert-success' : 'alert-error'}>
                    {message}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSavingProfile || !usernameAvailable || usernameError !== ''}
                  className="btn-primary w-full rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-body"
                >
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating profile...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Complete Setup
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer - only show on auth view for login */}
        {view === 'auth' && !isSignUp && (
          <p 
            className="text-center text-glass-muted text-sm mt-6 font-body"
          >
            By logging in, you agree to our{' '}
            <Link to="/terms" className="text-glass-secondary hover:underline">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-glass-secondary hover:underline">Privacy Policy</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Auth;
